// The fleet quota overview's view model (SCR-234, API-238, AllCode staff only) and the Overview
// tile built on it (NAV-002). Pure apart from the one read at the end, which takes the client
// (tests/unit/fleet.test.ts). The server ranks orgs by their fullest quota; the table re-sorts by
// any column. Staff see every money figure, but the fullest standing's amounts still pass the
// money policy (Q3), so a policy that hides them leaves the percentage alone.
import type { ApiClient } from "@nexus/ui";
import type { MoneyPolicy } from "@nexus/ui";
import type { components } from "../../api/schema";
import type { ChipTone } from "@nexus/ui";
import { amountText, bpPercentShort, stateLabel, stateTone, visibleAmount, type QuotaOwner, type QuotaState } from "./model";
import { quotasHref } from "@nexus/ui/screens/quotas/hrefs";

type Schemas = components["schemas"];
export type FleetQuotaStanding = Schemas["FleetQuotaStanding"];
export type FleetQuotaStandingItem = Schemas["FleetQuotaStandingItem"];

export const FLEET_PATH = "/quotas";
export const FLEET_TITLE = "Quota standing";

// ---------------------------------------------------------------------------------------------
// Filters (the API's own query parameters)

export const FLEET_STATES: readonly QuotaState[] = ["ok", "warning", "grace", "exhausted", "exempt"];
export const FLEET_OWNERS: readonly QuotaOwner[] = ["platform", "org"];

export interface FleetFilters {
  /** "" = every state. */
  state: QuotaState | "";
  /** "" = either owner. */
  owner: QuotaOwner | "";
}

export const NO_FLEET_FILTERS: FleetFilters = { state: "", owner: "" };

export type FleetQuery = { state?: QuotaState; owner?: QuotaOwner };

export function fleetQuery(filters: FleetFilters): FleetQuery {
  const query: FleetQuery = {};
  if (filters.state !== "") query.state = filters.state;
  if (filters.owner !== "") query.owner = filters.owner;
  return query;
}

export function ownerFilterLabel(owner: QuotaOwner): string {
  return owner === "platform" ? "With an AllCode guardrail" : "With organization quotas";
}

// ---------------------------------------------------------------------------------------------
// Rows

export interface FleetRow {
  orgId: string;
  orgName: string;
  payer: string;
  fullestName: string;
  fullestScope: string;
  /** "82%". */
  percent: string;
  /** 0..1 for the bar. */
  fraction: number;
  utilizationBp: number;
  /** "$82.00 of $100.00"; null when the policy hides the money. */
  amounts: string | null;
  stateLabel: string;
  stateTone: ChipTone;
  quotas: number;
  guardrailQuotas: number;
  warning: number;
  grace: number;
  exhausted: number;
  /** The org's quotas tab (SCR-230). */
  href: string;
}

export function payerLabel(awsPaidBy: Schemas["AwsPaidBy"]): string {
  return awsPaidBy === "allcode" ? "AllCode pays AWS" : "Customer pays AWS";
}

export function fleetRow(item: FleetQuotaStandingItem, policy: MoneyPolicy): FleetRow {
  const fullest = item.fullest;
  const consumed = visibleAmount(policy, fullest.metric, fullest.basis, fullest.consumed);
  const limit = visibleAmount(policy, fullest.metric, fullest.basis, fullest.effectiveLimit);
  return {
    orgId: item.orgId,
    orgName: item.orgName || item.orgId,
    payer: payerLabel(item.awsPaidBy),
    fullestName: fullest.name,
    fullestScope: fullest.scopeName,
    percent: bpPercentShort(fullest.utilizationBp),
    fraction: Math.min(Math.max(fullest.utilizationBp, 0), 10000) / 10000,
    utilizationBp: fullest.utilizationBp,
    amounts: consumed === null || limit === null ? null : `${amountText(fullest.metric, consumed)} of ${amountText(fullest.metric, limit)}`,
    stateLabel: stateLabel(fullest.state),
    stateTone: stateTone(fullest.state),
    quotas: item.quotas,
    guardrailQuotas: item.guardrailQuotas,
    warning: item.warning,
    grace: item.grace,
    exhausted: item.exhausted,
    href: quotasHref({ kind: "allcode-admin" }, item.orgId),
  };
}

/** In the server's order (fullest first). */
export function fleetRows(standing: FleetQuotaStanding, policy: MoneyPolicy): FleetRow[] {
  return standing.items.map((item) => fleetRow(item, policy));
}

// ---------------------------------------------------------------------------------------------
// Sorting (each comparator ascending; the table flips it)

export type FleetSortKey = "org" | "fullest" | "quotas" | "guardrailQuotas" | "warning" | "grace" | "exhausted";

type Numeric = "utilizationBp" | "quotas" | "guardrailQuotas" | "warning" | "grace" | "exhausted";
const byNumber = (field: Numeric) => (a: FleetRow, b: FleetRow) => a[field] - b[field] || a.orgName.localeCompare(b.orgName);

export const FLEET_COMPARE: Readonly<Record<FleetSortKey, (a: FleetRow, b: FleetRow) => number>> = {
  org: (a, b) => a.orgName.localeCompare(b.orgName),
  fullest: byNumber("utilizationBp"),
  quotas: byNumber("quotas"),
  guardrailQuotas: byNumber("guardrailQuotas"),
  warning: byNumber("warning"),
  grace: byNumber("grace"),
  exhausted: byNumber("exhausted"),
};

// ---------------------------------------------------------------------------------------------
// The Overview tile (NAV-002): how many orgs have a scope instance in each state that needs a look

export interface FleetTileCount {
  state: "exhausted" | "grace" | "warning";
  label: string;
  orgs: number;
  instances: number;
  tone: ChipTone;
}

export interface FleetTile {
  counts: readonly FleetTileCount[];
  /** Whether anything is exhausted or in grace. */
  attention: boolean;
  summary: string;
}

const TILE_STATES = [
  { state: "exhausted", label: "Exhausted", tone: "red" },
  { state: "grace", label: "In grace", tone: "amber" },
  { state: "warning", label: "Warning", tone: "amber" },
] as const;

function orgs(count: number): string {
  return `${count.toLocaleString("en-US")} ${count === 1 ? "organization" : "organizations"}`;
}

export function fleetTile(standing: FleetQuotaStanding): FleetTile {
  const counts = TILE_STATES.map(({ state, label, tone }) => ({
    state,
    label,
    tone,
    orgs: standing.items.filter((item) => item[state] > 0).length,
    instances: standing.items.reduce((sum, item) => sum + item[state], 0),
  }));
  const [exhausted, grace] = counts as [FleetTileCount, FleetTileCount, FleetTileCount];
  const attention = exhausted.orgs > 0 || grace.orgs > 0;
  let summary: string;
  if (standing.items.length === 0) summary = "No organization has a quota yet.";
  else if (!attention) summary = `${orgs(standing.items.length)} with quotas; none exhausted or in grace.`;
  else summary = `${orgs(exhausted.orgs)} exhausted, ${orgs(grace.orgs)} in grace.`;
  return { counts, attention, summary };
}

// ---------------------------------------------------------------------------------------------
// The read

export function loadFleet(client: ApiClient, filters: FleetFilters): Promise<FleetQuotaStanding> {
  const query = fleetQuery(filters);
  return client.get("/v1/admin/quota-standing", Object.keys(query).length > 0 ? { query } : {});
}

export function fleetCsvHref(client: ApiClient, filters: FleetFilters): string {
  return client.csvUrl("/v1/admin/quota-standing", { query: fleetQuery(filters) });
}
